import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, useToast } from '@chakra-ui/react';
import ModalComponent from '../Modal';
import ServerSideDatatable from '../ServerSideDatatable';
import { currancyFormatter } from '../../utils/helper';
import { getSiteAssociationMargins } from '../../redux/action/association.action';
import { TOAST_SETTINGS } from '../../constants';
import AssociationLosModal from './associationLosModal';

const ActionButtons = ({ site_id, site_name, handleOpenLosView }) => {
  return (
    <Button
      bg="main.semiPrimary"
      color="main.white"
      _hover={{ bg: 'main.primary' }}
      _active={{
        bg: 'main.semiPrimary',
        borderColor: 'main.primary',
      }}
      onClick={() => handleOpenLosView(site_id, site_name)}
    >
      View LOS
    </Button>
  );
};

const SiteAssociationModal = ({ isOpen, onClose, associationId }) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTable] = useState(false);
  const { user_details: userDetails } = useSelector((state) => state.authentication);
  const [isLosModalOpen, setIsLosModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);

  const handleOpenLosView = (site_id, site_name) => {
    setSelectedSite({ siteId: site_id, siteName: site_name });
    setIsLosModalOpen(true);
  };

  const handleCloseLosView = () => {
    setSelectedSite(null);
    setIsLosModalOpen(false);
  };

  const actionButtonRender = useMemo(
    () => (_cell, row) => {
      return ActionButtons({ site_id: row.site_id, site_name: row.site_name, handleOpenLosView });
    },
    []
  );

  const [columns] = useState([
    {
      label: 'Site name',
      field: 'site_name',
      sort: true,
    },
    {
      label: 'Margin',
      field: 'margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Overtime Margin',
      field: 'overtime_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Transport Fee',
      field: 'transport_fee',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Miscellaneous',
      field: 'ssp',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Training Margin',
      field: 'training_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Bh Margin',
      field: 'bh_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'NSP Margin',
      field: 'nsp_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Standard Margin',
      field: 'supervisor_standard_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Overtime Margin',
      field: 'supervisor_overtime_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Supervisor Permanent Margin',
      field: 'supervisor_permanent_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Suspension Margin',
      field: 'suspension_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Induction Training Margin',
      field: 'induction_training_margin',
      sort: false,
      customRow: (cell) => {
        return `${currancyFormatter(cell, userDetails.currency)}`;
      },
    },
    {
      label: 'Action',
      field: 'site_id',
      sort: false,
      customRow: actionButtonRender,
    },
  ]);

  const handlePaginate = useCallback(
    async (size, pageNumber, sortKey, sorting) => {
      setIsLoading(true);
      try {
        const result = await getSiteAssociationMargins(
          associationId,
          null,
          size,
          pageNumber,
          sortKey,
          sorting
        );
        if (result.ok) {
          return {
            data: {
              sites: result.data.margins_list,
              count: result.data.total,
            },
          };
        }
        toast({
          ...TOAST_SETTINGS.SETTINGS,
          status: TOAST_SETTINGS.ERROR,
          title: result.error,
        });
        return {
          data: {
            sites: [],
            count: 0,
          },
        };
      } catch (error) {
        return {
          data: {
            sites: [],
            count: 0,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [associationId]
  );

  return (
    <>
      <ModalComponent
        specifySize="6xl"
        isOpen={isOpen}
        onClose={onClose}
        modalTitle="Sites Associated"
        modalContent={
          <ServerSideDatatable
            pageSize={10}
            initialSortKey="site_name"
            column={columns}
            onPaginate={handlePaginate}
            afterPaginateData={() => setIsLoading(false)}
            pagination
            pages={[5, 10, 15]}
            dataKey="sites"
            refresh={refreshTable}
            isLoading={isLoading}
          />
        }
        negative={null}
        positive={null}
      />
      <AssociationLosModal
        isOpen={isLosModalOpen}
        onClose={handleCloseLosView}
        associationId={associationId}
        selectedSite={selectedSite}
      />
    </>
  );
};

export default SiteAssociationModal;
